/**
 * Utility functions for exporting chart and table data
 */

export interface ExportOptions {
  filename?: string;
  format: 'csv' | 'json' | 'png' | 'svg';
  includeHeaders?: boolean;
  dateFormat?: string;
}

/**
 * Convert data to CSV format
 */
function convertToCSV(data: any[], headers?: string[]): string {
  if (!data || data.length === 0) return '';

  const csvHeaders = headers || Object.keys(data[0]);
  const csvRows = data.map(row => 
    csvHeaders.map(header => {
      const value = row[header];
      // Handle values that might contain commas or quotes
      if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value ?? '';
    }).join(',')
  );

  return [csvHeaders.join(','), ...csvRows].join('\n');
}

/**
 * Download data as a file
 */
function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export chart data to CSV
 */
export function exportChartDataToCSV(data: any[], filename: string = 'chart-data.csv'): void {
  const csv = convertToCSV(data);
  downloadFile(csv, filename, 'text/csv');
}

/**
 * Export chart data to JSON
 */
export function exportChartDataToJSON(data: any[], filename: string = 'chart-data.json'): void {
  const json = JSON.stringify(data, null, 2);
  downloadFile(json, filename, 'application/json');
}

/**
 * Export table data with custom formatting
 */
export function exportTableData(
  data: any[], 
  columns: Array<{ key: string; title: string; render?: (value: any, row: any) => any }>,
  options: ExportOptions
): void {
  const { filename = 'table-data', format, includeHeaders = true } = options;

  // Transform data based on column definitions
  const transformedData = data.map(row => {
    const transformedRow: any = {};
    columns.forEach(column => {
      const value = row[column.key];
      transformedRow[column.title] = column.render ? column.render(value, row) : value;
    });
    return transformedRow;
  });

  switch (format) {
    case 'csv': {
      const headers = includeHeaders ? columns.map(col => col.title) : undefined;
      const csv = convertToCSV(transformedData, headers);
      downloadFile(csv, `${filename}.csv`, 'text/csv');
      break;
    }
    case 'json': {
      const json = JSON.stringify(transformedData, null, 2);
      downloadFile(json, `${filename}.json`, 'application/json');
      break;
    }
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
}

/**
 * Export chart as image (requires canvas conversion)
 */
export function exportChartAsImage(
  chartElement: HTMLElement, 
  filename: string = 'chart.png',
  format: 'png' | 'svg' = 'png'
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      if (format === 'svg') {
        // For SVG export, find the SVG element and export it
        const svgElement = chartElement.querySelector('svg');
        if (svgElement) {
          const svgData = new XMLSerializer().serializeToString(svgElement);
          const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
          const url = URL.createObjectURL(svgBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename.replace('.png', '.svg');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          resolve();
        } else {
          reject(new Error('SVG element not found'));
        }
      } else {
        // For PNG export, use html2canvas-like functionality
        // Since html2canvas is not available, we'll use a simpler approach
        const svgElement = chartElement.querySelector('svg');
        if (svgElement) {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas context not available'));
            return;
          }

          const svgData = new XMLSerializer().serializeToString(svgElement);
          const img = new Image();
          const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
          const url = URL.createObjectURL(svgBlob);

          img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            
            canvas.toBlob((blob) => {
              if (blob) {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                resolve();
              } else {
                reject(new Error('Failed to create image blob'));
              }
            }, 'image/png');
            
            URL.revokeObjectURL(url);
          };

          img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load SVG as image'));
          };

          img.src = url;
        } else {
          reject(new Error('SVG element not found'));
        }
      }
    } catch (error) {
      reject(error);
    }
  });
}